import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { useStore } from '../../store';
import { api, DropFolderFile } from '../../utils/api';
import { plLabel, LevelLabel } from '../Jobs';
import { jobTitleLabel } from '../../utils/displayName';
import DropFolderPicker, { dateInName } from '../Jobs/DropFolderPicker';
import DraftBody, { DraftBodyHandle } from '../DraftBody';
import PopupHomeNotice from '../PopupHomeNotice';
import { usePopupHome, HOME_JOBS } from '../../services/popupHome';   // Active Jobs, one tab only (report c2846304)
import { popupsApi, splitEmails } from '../../utils/api/popups';
import { jcDraftStep, jcCanSend, jcConfirmPayload, JcDraft, JcSeeded } from './emailDraft';

// Global watcher for deferred job-status-change confirmations. A status change queues a
// confirmation server-side; 10 minutes later this popup asks the operator who made the change to
// re-verify the status / finish date before the email is actually sent. Confirm → send; Re-check
// → open the job editor (and re-arm the 10-min timer); Cancel → re-arm the timer. It reappears
// every 10 minutes until confirmed. Mounted once, app-wide.

const POLL_MS = 60_000;
const fmtDate = (ymd: string) => { const p = String(ymd || '').split('-'); return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : (ymd || '—'); };
// A date whole enough to file a waiting pick by itself (CompletionDocs' pending): the box's shape, a real day and a
// year from 1900 to 2099 (what dateInName reads), so a year typed digit by digit — 0002, 0020, 0202 — files nothing.
const wholeDate = (d: string) => !!d && dateInName(d) === d;
// How long a typed date must stand before the waiting pick is filed with it: on its way to 2026-09-19 the box
// can hold 2026-09-01 (the day typed last), a whole date that is not the one meant.
const DATE_SETTLE_MS = 1000;

// ── 完成 Job 的必收文档 (Calem 2026-08-25 → owner 2026-09-10) ────────────────────────────────────
// Marking a job Complete tells the client it is done, so the proof is collected HERE, before the
// green button exists. Each row states the name the file will be filed under (composed from the
// four-part protocol, editable before saving — owner decision 1) and the folder in Our Documents
// it will land in, so the operator sees both without leaving the popup.
function CompletionDocs({ jobId, onState, onPlan }: { jobId: string; onState: (s: any) => void; onPlan: (p: any) => void }) {
  const { t } = useTranslation();
  const [state, setState] = useState<any>(null);
  const [busyKey, setBusyKey] = useState('');
  const [err, setErr] = useState('');
  // `auto`: the date a picked file's name put in the row's date box. While the box still holds it, the next
  // file picked brings its own date; a date typed over it is the operator's and stays.
  const [draft, setDraft] = useState<Record<string, { name?: string; date?: string; company?: string; auto?: string }>>({});
  // The row whose file is being picked from the Drop Folder — the only place these documents come
  // from (Calem, report 296f15b9: "Only from the Drop Folder").
  const [picking, setPicking] = useState<any>(null);
  // The file picked for a row, held until its date is in (report 02661349, 3rd opening: "allow the user to
  // upload without putting the date in first"). Every row but the Re-entry Stamp's, which reentryDraft holds.
  const [pending, setPending] = useState<Record<string, DropFolderFile>>({});
  // A date typed in while a pick waits files it once it has stood DATE_SETTLE_MS (one timer per row);
  // fileWhenDue is the latest render's, so the timer files the row as it is by then.
  const dateTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const fileWhenDue = useRef<(key: string) => void>(() => {});
  useEffect(() => { const timers = dateTimers.current; return () => { Object.values(timers).forEach(id => clearTimeout(id)); }; }, []);
  // The rows being filed right now, so the timer and the Upload button never send one file twice.
  const filing = useRef<Set<string>>(new Set());
  // Re-entry Stamp only: the picked file is held here, classified, until the operator confirms or
  // corrects Single/Multi — never filed straight off the pick like every other row (bug
  // 4275c61e: the old flow saved the ambiguous "Single or Multi" label with no check at all).
  // 'none' (report 603af598): the operator picked the "NO Re-entry Stamp" naming example — the client
  // has no stamp, so there is no Single/Multi to choose, and the example stays in the Drop Folder.
  const [reentryDraft, setReentryDraft] = useState<{ node: DropFolderFile; kind: '' | 'single' | 'multi' | 'none'; detected: '' | 'single' | 'multi' | 'none'; source: string } | null>(null);

  // The reminders these documents earn. Same plan the Document Storage wizard shows, and shown for
  // the same reason (owner 2026-08-25, restated 2026-09-10): nothing is written to the calendar
  // blindly — the operator sees every event first and can retime or drop any of it.
  const [plan, setPlan] = useState<any[]>([]);
  const load = useCallback(async () => {
    try { const s: any = await api.jobCompletionDocs(jobId); setState(s); onState(s); } catch { /* leave as is */ }
  }, [jobId, onState]);
  useEffect(() => { void load(); }, [load]);

  const uploaded = (state?.rows || []).filter((r: any) => r.uploaded);
  // Only expiry-dated documents belong on the calendar. An issued-dated one (Company Documents, a
  // licence receipt) carries the day it was MADE — turning that into an "EXPIRY —" event would put
  // a false deadline in office@'s calendar (Calem batch 2026-09-10 added the first issued docs).
  // A "NO Re-entry Stamp" proof (noStamp, report 603af598) has no stamp to expire, so it stays off too.
  const expiring = uploaded.filter((r: any) => r.dateKind === 'expiry' && !r.uploaded.noStamp);
  useEffect(() => {
    if (!expiring.length) { setPlan([]); onPlan(null); return; }
    const items = expiring.map((r: any) => ({ docType: r.docName, expiryIso: r.uploaded.dateIso }));
    api.docStorageExpiryPlan(items).then((d: any) => {
      const rows = (d.plans || []).map((p: any, i: number) => ({
        docType: items[i].docType, expiryIso: items[i].expiryIso, includeExpiry: true,
        reminders: (p.reminders || []).map((x: any) => ({ ...x, on: true })),
      }));
      setPlan(rows);
    }).catch(() => setPlan([]));
  }, [JSON.stringify(expiring.map((r: any) => [r.docName, r.uploaded.dateIso]))]); // eslint-disable-line

  useEffect(() => {
    onPlan(plan.length ? { items: plan.map(p => ({
      docType: p.docType, expiryIso: p.expiryIso, includeExpiry: p.includeExpiry,
      reminders: p.reminders.filter((r: any) => r.on).map((r: any) => ({ days: r.days, note: r.note })),
    })) } : null);
  }, [JSON.stringify(plan)]); // eslint-disable-line

  if (!state || !state.gated) return null;

  const nameFor = (r: any) => draft[r.key]?.name ?? r.suggestedName;
  const dateFor = (r: any) => draft[r.key]?.date ?? '';
  // A Company TIN's company part (Calem, report a97c67be): prefilled from the client, typed for a person.
  const companyFor = (r: any) => String(draft[r.key]?.company ?? r.company ?? '');
  const companyMissing = (r: any) => !!r.needsCompany && !companyFor(r).trim();
  // Calem 2026-09-10: the name people read is day-first (21-10-2026); ISO stays in the database.
  // ISO in filenames (Calem 2026-09-16, reversing 2026-09-10) — mirrors the server's nameDate.
  const nameDate = (iso: string) => (/^\d{4}-\d{2}-\d{2}$/.test(iso) ? iso : '');
  // The string the server will write, to the letter — shown under the row so the operator checks
  // it BEFORE the file lands (his bug: the entered date never reached the saved name). `date`: the
  // one a pick has just put in the box, which this render's draft does not hold yet.
  const finalNameFor = (r: any, date = dateFor(r)) => {
    let base = String(nameFor(r) || r.suggestedName || '').trim();
    const d = nameDate(date);
    const legacy = d ? d.split('-').reverse().join('-') : '';
    if (legacy && base.includes(legacy)) base = base.split(legacy).join(d);   // typed day-first → ISO
    // The Company box's text goes after the person and before the date, unless the name has it there.
    const co = r.needsCompany ? companyFor(r).trim() : '';
    const tail = d && base.endsWith(` ${d}`) ? ` ${d}` : '';
    const head = base.slice(0, base.length - tail.length);
    if (co && !head.endsWith(` ${co}`)) base = `${head} ${co}${tail}`;
    return d && !base.includes(d) ? `${base} ${d}` : base;
  };
  // Only the fields given change: a date put in the box leaves the name box on its suggested name.
  const setD = (k: string, patch: { name?: string; date?: string; company?: string; auto?: string }) => setDraft(d => ({ ...d, [k]: { ...d[k], ...patch } }));

  // Files the row's picked file under the name shown; true once it is in. A row being filed already, or
  // still without its date or company, files nothing (the last two say why).
  const upload = async (r: any, node: DropFolderFile, date = dateFor(r)): Promise<boolean> => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) { setErr(t('jc_doc_need_date', { kind: r.dateKind })); return false; }
    if (companyMissing(r)) { setErr(t('jc_doc_need_company')); return false; }
    if (filing.current.has(r.key)) return false;
    filing.current.add(r.key);
    clearTimeout(dateTimers.current[r.key]);
    setBusyKey(r.key); setErr('');
    try {
      const out: any = await api.jobCompletionDocUpload(jobId, node.id, {
        reqKey: r.key, docName: finalNameFor(r, date), dateIso: date, folderId: r.folderId,
        company: r.needsCompany ? companyFor(r).trim() : undefined,
      });
      setPending(p => { const next = { ...p }; delete next[r.key]; return next; });
      setState(out.state); onState(out.state);
      return true;
    } catch (e: any) { setErr(e?.message || 'Upload failed'); return false; }
    finally { filing.current.delete(r.key); setBusyKey(''); }
  };
  // The file before the date (report 02661349, 3rd opening). The picked file's name fills the date box when
  // the box is empty or still holds the date an earlier pick brought (auto); a date the operator typed
  // stays. A naming example's name brings none — its date is a sample. Hands back the date the row now has.
  const prefill = (r: any, node: DropFolderFile): string => {
    const cur = dateFor(r);
    if (cur && cur !== draft[r.key]?.auto) return cur;
    const found = node.example ? '' : dateInName(node.name);
    if (found !== cur) setD(r.key, { date: found, auto: found });
    return found;
  };
  // A picked file waits on its row (pending) until its date — and a Company TIN's company — is in, then is
  // filed: at once when the pick already has them, as it always was; once a typed date has stood (see
  // fileOnceDated); or with the row's Upload button.
  const choose = (r: any, node: DropFolderFile) => {
    setPending(p => ({ ...p, [r.key]: node }));
    setErr('');
    const date = prefill(r, node);
    if (wholeDate(date) && !companyMissing(r)) void upload(r, node, date);
  };
  const unpick = (r: any) => {
    clearTimeout(dateTimers.current[r.key]);
    setPending(p => { const next = { ...p }; delete next[r.key]; return next; });
    setErr('');
  };
  const fileOnceDated = (k: string) => {
    clearTimeout(dateTimers.current[k]);
    if (pending[k]) dateTimers.current[k] = setTimeout(() => fileWhenDue.current(k), DATE_SETTLE_MS);
  };
  fileWhenDue.current = (k: string) => {
    const r = state.rows.find((x: any) => x.key === k);
    if (r && pending[k] && !r.uploaded && wholeDate(dateFor(r)) && !companyMissing(r)) void upload(r, pending[k]);
  };
  const drop = async (r: any) => {
    setBusyKey(r.key);
    try { const out: any = await api.jobCompletionDocRemove(jobId, r.uploaded.id); setState(out.state); onState(out.state); }
    catch (e: any) { setErr(e?.message || 'Failed'); }
    finally { setBusyKey(''); }
  };

  // Single vs Multi: swap the qualifier onto whatever name is currently drafted, so an operator's
  // own edits to the person/company parts survive a Single↔Multi toggle.
  const REENTRY_NAME_RX = /^Re-entry Stamp(?: (?:Single or Multi|Single|Multi))?/;
  const withReentryKind = (name: string, kind: 'single' | 'multi') =>
    String(name || '').replace(REENTRY_NAME_RX, `Re-entry Stamp ${kind === 'single' ? 'Single' : 'Multi'}`);
  // The stamp is read first and its date asked after (report 02661349, 3rd opening): the name comes back
  // without one, and finalNameFor adds whatever date the box holds when Confirm & file is pressed.
  const pickReentry = async (r: any, node: DropFolderFile) => {
    prefill(r, node);
    setBusyKey(r.key); setErr('');
    try {
      const out: any = await api.jobReentryClassify(jobId, node.id, '');
      const kind = (out.kind === 'single' || out.kind === 'multi' || out.kind === 'none') ? out.kind : '';
      setReentryDraft({ node, kind, detected: kind, source: out.source || 'none' });
      setD(r.key, { name: out.suggestedName || nameFor(r) });
    } catch (e: any) { setErr(e?.message || 'Analysis failed'); }
    finally { setBusyKey(''); }
  };
  const chooseReentryKind = (r: any, kind: 'single' | 'multi') => {
    setReentryDraft(d => (d ? { ...d, kind } : d));
    setD(r.key, { name: withReentryKind(nameFor(r), kind) });
  };
  // Never filed by itself — Single/Multi is the operator's to confirm (bug 4275c61e). A date still missing
  // keeps the reading here until it is typed in.
  const confirmReentry = async (r: any) => {
    if (!reentryDraft || !reentryDraft.kind) return;
    if (await upload(r, reentryDraft.node)) setReentryDraft(null);
  };

  const inp: React.CSSProperties = { padding: '5px 8px', fontSize: 11.5, borderRadius: 7, border: '1px solid var(--border-strong)', background: 'var(--bg-app)', color: 'var(--text-primary)' };
  return (
    <div style={{ margin: '10px 22px', padding: '12px 14px', border: '1px solid var(--border)', borderRadius: 10, background: 'var(--bg-app)' }}>
      <div style={{ fontSize: 12, fontWeight: 800, marginBottom: 2 }}>{t('jc_doc_title')}</div>
      <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginBottom: 9 }}>{t('jc_doc_hint')}</div>
      {state.rows.map((r: any) => (
        <div key={r.key} style={{ borderTop: '1px solid var(--border)', padding: '9px 0' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12.5, fontWeight: 700 }}>
            <i className={r.uploaded ? 'ti ti-circle-check-filled' : r.required ? 'ti ti-alert-circle' : 'ti ti-circle'}
              style={{ fontSize: 14, color: r.uploaded ? '#15803D' : r.required ? '#B45309' : 'var(--text-tertiary)' }} aria-hidden />
            {r.docName}
            <span style={{ fontSize: 10, fontWeight: 800, padding: '1px 6px', borderRadius: 999, background: r.required ? 'rgba(180,83,9,0.12)' : 'var(--c-gray-100)', color: r.required ? '#B45309' : 'var(--text-tertiary)' }}>
              {r.required ? t('jc_doc_required') : t('jc_doc_optional')}
            </span>
          </div>
          {r.uploaded ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 5, fontSize: 11.5 }}>
              <span style={{ color: '#15803D', fontWeight: 700 }}>{r.uploaded.docName}</span>
              <span style={{ color: 'var(--text-tertiary)' }}>→ {r.folderPath || t('jc_doc_no_folder')}</span>
              <button onClick={() => drop(r)} disabled={busyKey === r.key}
                style={{ border: 'none', background: 'transparent', color: '#E24B4A', cursor: 'pointer', fontWeight: 800 }}>×</button>
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 5, flexWrap: 'wrap' }}>
              <input value={nameFor(r)} onChange={e => setD(r.key, { name: e.target.value })}
                title={t('jc_doc_name_tip')} style={{ ...inp, flex: '2 1 260px' }} />
              <input type="date" value={dateFor(r)} onChange={e => { setD(r.key, { date: e.target.value }); fileOnceDated(r.key); }}
                title={t('jc_doc_date_tip', { kind: r.dateKind })} style={{ ...inp, width: 140 }} />
              {/* A Company TIN names a company (Calem, report a97c67be) — the client's, or typed here for
                  a person. data-company-box: the one box here that must be typed in, so its keys do not
                  hide the popup (the `typing` check below). */}
              {r.needsCompany && (
                <label style={{ flex: '1 1 200px', display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
                  {t('jc_doc_company')}
                  <input data-company-box value={companyFor(r)} onChange={e => setD(r.key, { company: e.target.value })}
                    title={t('jc_doc_company_tip')} style={{ ...inp, flex: 1, minWidth: 0 }} />
                </label>
              )}
              <div style={{ flexBasis: '100%', fontSize: 11, marginTop: 2 }}>
                <span style={{ color: 'var(--text-tertiary)' }}>{t('jc_doc_stored_as')} </span>
                <b style={{ color: dateFor(r) && !companyMissing(r) ? '#15803D' : '#B45309' }}>{finalNameFor(r)}{companyMissing(r) ? ` — ${t('jc_doc_company_pending')}` : ''}{dateFor(r) ? '' : ` — ${t('jc_doc_date_pending', { kind: r.dateKind })}`}</b>
              </div>
              {r.key === 'reentry' && reentryDraft ? (
                <div style={{ flexBasis: '100%', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 3, padding: '7px 9px', borderRadius: 8, background: 'var(--c-gray-50)', border: '1px solid var(--border)' }}>
                  <span style={{ flexBasis: '100%', fontSize: 11.5, fontWeight: 700, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    <i className="ti ti-folder-open" style={{ marginRight: 5, color: 'var(--accent)' }} aria-hidden />{t('jdp_picked', { name: reentryDraft.node.name })}
                  </span>
                  <span style={{ fontSize: 11.5, color: 'var(--text-secondary)' }}>
                    {reentryDraft.kind === 'none'
                      ? t('jc_reentry_none')
                      : !reentryDraft.detected
                        ? t('jc_reentry_unclear')
                        : reentryDraft.kind === reentryDraft.detected
                          ? t(reentryDraft.source === 'filename' ? 'jc_reentry_detected_name' : 'jc_reentry_detected_stamp', { value: t(reentryDraft.kind === 'single' ? 'jc_reentry_single' : 'jc_reentry_multi') })
                          : t('jc_reentry_corrected', { value: t(reentryDraft.kind === 'single' ? 'jc_reentry_single' : 'jc_reentry_multi') })}
                  </span>
                  {reentryDraft.kind !== 'none' && (
                    <div style={{ display: 'flex', gap: 5 }}>
                      {(['single', 'multi'] as const).map(k => (
                        <button key={k} type="button" onClick={() => chooseReentryKind(r, k)} disabled={busyKey === r.key}
                          style={{ padding: '4px 10px', borderRadius: 999, fontSize: 11, fontWeight: 700, cursor: 'pointer',
                            border: reentryDraft.kind === k ? '1px solid var(--accent)' : '1px solid var(--border-strong)',
                            background: reentryDraft.kind === k ? 'var(--accent)' : 'transparent',
                            color: reentryDraft.kind === k ? '#fff' : 'var(--text-primary)' }}>
                          {t(k === 'single' ? 'jc_reentry_single' : 'jc_reentry_multi')}
                        </button>
                      ))}
                    </div>
                  )}
                  <button onClick={() => confirmReentry(r)} disabled={!reentryDraft.kind || busyKey === r.key}
                    style={{ padding: '5px 12px', borderRadius: 7, border: 'none', background: 'var(--accent)', color: '#fff', fontSize: 11.5, fontWeight: 700, cursor: reentryDraft.kind ? 'pointer' : 'not-allowed', opacity: reentryDraft.kind ? 1 : 0.6 }}>
                    {busyKey === r.key ? '…' : t('jc_reentry_confirm')}
                  </button>
                  <button onClick={() => setReentryDraft(null)} disabled={busyKey === r.key}
                    style={{ border: 'none', background: 'transparent', color: 'var(--text-tertiary)', fontSize: 11, cursor: 'pointer', textDecoration: 'underline' }}>
                    {t('jc_reentry_cancel')}
                  </button>
                </div>
              ) : pending[r.key] ? (
                // The picked file waiting for its date (or its company), shown as the Re-entry Stamp's is:
                // filed by itself once the date is in (see choose), or now with Upload.
                <div style={{ flexBasis: '100%', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 3, padding: '7px 9px', borderRadius: 8, background: 'var(--c-gray-50)', border: '1px solid var(--border)' }}>
                  <span style={{ flexBasis: '100%', fontSize: 11.5, fontWeight: 700, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    <i className="ti ti-folder-open" style={{ marginRight: 5, color: 'var(--accent)' }} aria-hidden />{t('jdp_picked', { name: pending[r.key].name })}
                  </span>
                  <button onClick={() => { void upload(r, pending[r.key]); }} disabled={busyKey === r.key}
                    style={{ padding: '5px 12px', borderRadius: 7, border: 'none', background: 'var(--accent)', color: '#fff', fontSize: 11.5, fontWeight: 700, cursor: 'pointer' }}>
                    {busyKey === r.key ? '…' : t('fl_upload')}
                  </button>
                  <button onClick={() => unpick(r)} disabled={busyKey === r.key}
                    style={{ border: 'none', background: 'transparent', color: 'var(--text-tertiary)', fontSize: 11, cursor: 'pointer', textDecoration: 'underline' }}>
                    {t('jc_reentry_cancel')}
                  </button>
                  <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>→ {r.folderPath || t('jc_doc_no_folder')}</span>
                </div>
              ) : (
                <>
                  {/* The file first, its date after (report 02661349, 3rd opening: "allow the user to upload
                      without putting the date in first") — see choose and pickReentry. */}
                  <button onClick={() => { setErr(''); setPicking(r); }}
                    disabled={busyKey === r.key}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '5px 12px', borderRadius: 7, border: '1px solid var(--border-strong)', background: 'var(--bg-surface)', fontSize: 11.5, fontWeight: 700, cursor: 'pointer' }}>
                    <i className="ti ti-folder-open" style={{ fontSize: 13 }} aria-hidden />{busyKey === r.key ? '…' : t('jdp_choose')}
                  </button>
                  <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>→ {r.folderPath || t('jc_doc_no_folder')}</span>
                </>
              )}
            </div>
          )}
        </div>
      ))}
      {err && <div style={{ fontSize: 11.5, color: '#B42318', marginTop: 8 }}>{err}</div>}
      {!!plan.length && (
        <div style={{ borderTop: '1px solid var(--border)', marginTop: 6, paddingTop: 9 }}>
          <div style={{ fontSize: 11.5, fontWeight: 800, marginBottom: 5 }}>
            <i className="ti ti-calendar-plus" style={{ marginRight: 5 }} />{t('jc_cal_title')}
          </div>
          {plan.map((p, i) => (
            <div key={p.docType} style={{ marginBottom: 6 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11.5, fontWeight: 700, cursor: 'pointer' }}>
                <input type="checkbox" checked={p.includeExpiry}
                  onChange={e => setPlan(ps => ps.map((x, j) => (j === i ? { ...x, includeExpiry: e.target.checked } : x)))} />
                {t('jc_cal_expiry', { type: p.docType, date: String(p.expiryIso).split('-').reverse().join('/') })}
              </label>
              {p.reminders.map((r: any, k: number) => (
                <label key={k} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, marginLeft: 18, marginTop: 3, cursor: 'pointer', color: 'var(--text-secondary)' }}>
                  {/* A STANDARD lead (std, from the server's schedule) is not the operator's to
                      adjust — or to untick (owner 2026-09-16). Hand-added ones stay editable. */}
                  <input type="checkbox" checked={r.on} disabled={!!r.std}
                    onChange={e => setPlan(ps => ps.map((x, j) => (j === i ? { ...x, reminders: x.reminders.map((y: any, m: number) => (m === k ? { ...y, on: e.target.checked } : y)) } : x)))} />
                  {r.std ? (
                    <b title="Standard lead time — fixed for this document type" style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                      {r.days}<i className="ti ti-lock" style={{ fontSize: 10, opacity: .55 }} aria-hidden />
                    </b>
                  ) : (
                  <input type="number" min={1} max={600} value={r.days}
                    onChange={e => setPlan(ps => ps.map((x, j) => (j === i ? { ...x, reminders: x.reminders.map((y: any, m: number) => (m === k ? { ...y, days: Math.max(1, Number(e.target.value) || 1) } : y)) } : x)))}
                    style={{ width: 62, padding: '2px 5px', fontSize: 11, borderRadius: 6, border: '1px solid var(--border-strong)', background: 'var(--bg-app)', color: 'var(--text-primary)' }} />
                  )}
                  {t('jc_cal_days_before')}{r.note ? ` · ${r.note}` : ''}
                  {r.custom && (
                    <span onClick={() => setPlan(ps => ps.map((x, j) => (j === i ? { ...x, reminders: x.reminders.filter((_: any, m: number) => m !== k) } : x)))}
                      style={{ cursor: 'pointer', color: '#E24B4A', fontWeight: 800, marginLeft: 2 }}>×</span>
                  )}
                </label>
              ))}
              {!p.reminders.length && <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginLeft: 18 }}>{t('jc_cal_none')}</div>}
              {/* A type with no standard schedule (a Re-entry Stamp, whose validity follows whatever
                  visa it sits in) still deserves a reminder — the operator sets it here rather than
                  being sent to the calendar afterwards (owner 2026-09-10). No lead time is invented:
                  the row starts blank-ish and the person who knows the document fills it in. */}
              <button type="button" onClick={() => setPlan(ps => ps.map((x, j) => (j === i
                ? { ...x, reminders: [...x.reminders, { days: 42, note: '', on: true, custom: true }] } : x)))}
                style={{ marginLeft: 18, marginTop: 4, padding: '2px 8px', fontSize: 10.5, fontWeight: 700, borderRadius: 6, border: '1px dashed var(--border-strong)', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer' }}>
                + {t('jc_cal_add')}
              </button>
            </div>
          ))}
        </div>
      )}
      {!!state.missing.length && (
        <div style={{ fontSize: 11.5, color: '#B45309', marginTop: 8, fontWeight: 700 }}>
          <i className="ti ti-lock" style={{ marginRight: 5 }} />{t('jc_doc_blocked', { list: state.missing.join(', ') })}
        </div>
      )}
      {picking && (
        <DropFolderPicker docName={picking.docName} onClose={() => setPicking(null)} examples={picking.key === 'reentry'}
          onPick={f => { const r = picking; setPicking(null); if (r.key === 'reentry') void pickReentry(r, f); else choose(r, f); }} />
      )}
    </div>
  );
}

export default function JobStatusConfirm() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const loc = useLocation();
  const me = useStore(s => s.me);
  // The Complete gate: while a required proof is missing the green button is not rendered at all
  // (owner 2026-09-10 — hard, no override). Jobs whose type is not in the catalogue report
  // gated:false and behave exactly as before.
  const [docState, setDocState] = useState<any>(null);
  const [calPlan, setCalPlan] = useState<any>(null);
  const showToast = useStore(s => s.showToast);
  const setBlockingError = useStore(s => s.setBlockingError);
  // It used to surface ONLY on a Jobs page, so an operator who set a status and then went back to
  // their mailbox never saw the popup — the client was never told the job was complete and the
  // confirmation sat there until it expired after 7 days. Owner 2026-08-24: pop it wherever the
  // operator is working. The public/no-auth pages are still excluded: a client on /w/<token> or a
  // signer on /contract/<token> must never be shown internal job confirmations.
  const publicPage = /^\/(login|w|dd|contract|portal)(\/|$)/.test(loc.pathname);
  // Don't steal the keyboard from someone mid-sentence — an email composer losing focus (or eating
  // an Enter) is exactly how a reminder turns into a complaint. It re-checks every poll and on blur,
  // so this only postpones the popup by seconds, never drops it.
  // ⚠️ This must key on ACTUAL KEYSTROKES, not on focus. Checking `document.activeElement` was an
  // input suppressed the popup permanently — plenty of pages autofocus a search box, so the
  // condition was true forever and nobody ever saw a confirmation (regression 2026-08-24, caught
  // within the hour). A pause of TYPING_IDLE_MS with no key pressed is enough.
  const TYPING_IDLE_MS = 4000;
  const [typing, setTyping] = useState(false);
  const lastKey = useRef(0);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement | null;
      // Searching the Drop Folder picker (CompletionDocs) is work inside this popup — hiding it would
      // close the picker under the operator's fingers. So is a Company box (report a97c67be): it can
      // only be filled by typing, and hiding the popup drops what was typed.
      // The popup is the email's editor too (owner 2026-10-03): typing anywhere inside it is work in it.
      if (el?.closest?.('[data-drop-picker], [data-company-box], [data-jc-popup]')) return;
      const tag = (el?.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || el?.isContentEditable) { lastKey.current = Date.now(); setTyping(true); }
    };
    document.addEventListener('keydown', onKey, true);
    // Falls back to false on its own — never a state something can get stuck in.
    const id = setInterval(() => setTyping(Date.now() - lastKey.current < TYPING_IDLE_MS), 1000);
    return () => { document.removeEventListener('keydown', onKey, true); clearInterval(id); };
  }, []);
  const [queue, setQueue] = useState<any[]>([]);
  const [busy, setBusy] = useState(false);
  const meRef = useRef(me);
  meRef.current = me;

  // Hands the rows back too, for "Reload the email" (undefined: not polled / failed).
  const poll = useCallback(async (): Promise<any[] | undefined> => {
    if (!meRef.current || !localStorage.getItem('mf_token')) return undefined;
    try {
      const { rows } = await api.jobConfirmMine();
      setQueue(rows || []);
      return rows || [];
    } catch { return undefined; /* transient — try again next tick */ }
  }, []);

  useEffect(() => {
    poll();
    const timer = setInterval(poll, POLL_MS);
    return () => clearInterval(timer);
  }, [poll]);

  // Drop everything the moment the user logs out — never linger over the login screen with the
  // previous user's job/client data.
  useEffect(() => { if (!me) setQueue([]); }, [me]);
  // Refresh the queue right when the operator lands on a Jobs page (don't wait for the 60s tick).
  useEffect(() => { poll(); }, [loc.pathname, poll]);   // a fresh check whenever the operator moves around

  const current = queue[0];

  // ── Workflow-deliverables composer state ──────────────────────────────────────────────────────
  // Declared BEFORE the early return — hooks must run in the same order on every render, and the
  // popup returns null whenever the queue is empty.
  const [extra, setExtra] = useState('');
  const [picked, setPicked] = useState<string[]>([]);
  const curId = current?.id || '';
  const curWf = current?.wf as undefined | {
    linked: boolean; link: string; files: Array<{ id: string; name: string; size: number; step: string }>;
    totalSize: number; overCap: boolean; cap: number; defaultHtml: string;
  };
  // Re-seed whenever a different confirmation reaches the front of the queue.
  useEffect(() => {
    setExtra(curWf?.defaultHtml || '');
    setPicked(curWf && !curWf.overCap ? curWf.files.map(f => f.id) : []);
  }, [curId]);   // eslint-disable-line react-hooks/exhaustive-deps

  // ── The email editor (owner 2026-10-03: every outbound email opens an editor first) ──────────
  // The server sends the draft with each row (`draft`: From / To / Cc / Subject / Body exactly as
  // Confirm would send it now, and `sig`). The fields are filled from ONE draft (`seeded`) and Confirm
  // sends that draft's sig, so the server refuses the edits once the job has moved on (409). What
  // happens on each poll is decided in emailDraft.ts (jcDraftStep), which the package test runs.
  const curDraft = current?.draft as JcDraft | undefined;
  const [eTo, setETo] = useState('');
  const [eCc, setECc] = useState('');
  const [eSubj, setESubj] = useState('');
  const [eBody, setEBody] = useState('');
  const [eDirty, setEDirty] = useState(false);
  const [eErr, setEErr] = useState('');
  // The job moved while the fields held edits, or Confirm came back 409: Confirm is off until the
  // operator presses "Reload the email" — their edits are replaced only after they have been told.
  const [eStale, setEStale] = useState(false);
  const [eReloaded, setEReloaded] = useState(false);   // "the job changed, so the email was reloaded"
  // Bumped on every (re)fill: remounts the body editor, so what was typed in it never outlives the
  // draft it was typed on (a new draft with the same body string would not reset the DOM otherwise).
  const [eBodyKey, setEBodyKey] = useState(0);
  const seeded = useRef<JcSeeded>({ id: '', sig: '' });
  const bodyRef = useRef<DraftBodyHandle | null>(null);
  const fillFrom = (id: string, d: JcDraft | undefined, changed: boolean) => {
    seeded.current = { id, sig: d?.sig || '' };
    setETo(d ? d.to.join(', ') : ''); setECc(d ? d.cc.join(', ') : ''); setESubj(d?.subject || ''); setEBody(d?.bodyHtml || '');
    setEDirty(false); setEStale(false); setEErr(''); setEReloaded(changed && !!d); setEBodyKey(k => k + 1);
  };
  useEffect(() => {
    const step = jcDraftStep(seeded.current, curId, curDraft, eDirty, eStale);
    if (step.kind === 'seed') fillFrom(curId, curDraft, step.changed);
    else if (step.kind === 'clear') fillFrom(curId, undefined, false);
    else if (step.kind === 'refresh' && curDraft) setEBody(curDraft.bodyHtml);   // same job state: only the "Updated by" stamp moved
    else if (step.kind === 'stale') setEStale(true);
  }, [curId, curDraft?.sig, curDraft?.bodyHtml]);   // eslint-disable-line react-hooks/exhaustive-deps

  // Report c2846304: this confirmation is answered on Active Jobs, so it is asked in the tab showing that
  // page and in no other. With the page open in no tab, the tab in front offers to open it (PopupHomeNotice).
  const pending = !!me && !!current && !publicPage && !typing;
  const home = usePopupHome(HOME_JOBS, pending);
  if (!pending) return null;
  if (home !== 'show') return home === 'notice' ? <PopupHomeNotice page={HOME_JOBS} /> : null;

  const client = `${current.first_name || ''} ${current.last_name || ''}`.trim() || '—';
  const finish = current.finish_unknown ? t('jc_unknown') : fmtDate(current.finish_date);
  const finishChanged = Number(current.finish_changed) === 1;
  const oldFinishStr = current.old_finish_unknown ? t('jc_unknown')
    : (String(current.old_finish || '').trim() ? fmtDate(current.old_finish) : '—');
  // When the finish date is what changed, make it stand out (amber highlight + "was …") so the
  // operator re-verifies the new date specifically.
  const finishNode: React.ReactNode = finishChanged ? (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
      <span style={{ background: '#FEF3C7', color: '#92400E', border: '1px solid #FCD34D', borderRadius: 6, padding: '2px 9px', fontWeight: 800 }}>{finish}</span>
      <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: 0.3, color: '#B45309', background: '#FEF3C7', border: '1px solid #FCD34D', borderRadius: 999, padding: '1px 7px' }}>{t('jc_changed')}</span>
      <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{t('jc_was', { v: oldFinishStr })}</span>
    </span>
  ) : finish;
  // To/Cc are the server's — worked out by the same rule the send uses (Calem, report c20d80d4), so
  // the box never names people the email does not reach. When the client is left out (a status they
  // are not told about, a date change outside 2. In Process, no address on the job) the office
  // mailbox is the To and the box and the subtitle say it is internal only. `resources` are the job's
  // resources Confirm mails each on their own about a new finish date (report 61ff7493).
  const rc = current.recipients as undefined | { to: string[]; cc: string[]; includeCustomer: boolean; resources?: string[] };
  const internalOnly = !!rc && !rc.includeCustomer;
  const to = rc?.to?.length ? rc.to.join(', ') : '—';
  const cc = rc?.cc?.length ? rc.cc.join(', ') : '—';
  const drop = (id: string) => setQueue(q => q.filter(x => x.id !== id));

  // ── Workflow deliverables (owner 2026-08-24) ──────────────────────────────────────────────────
  // Present only when the server decided this row qualifies: a workflow-linked job moving to
  // 4. Complete. The operator edits the paragraph and which files go (the subject is the email
  // editor's, below) — or presses
  // "Status Only" to send the plain status email with nothing attached.
  const wf = curWf;
  const pickedSize = (wf?.files || []).filter(f => picked.includes(f.id)).reduce((n, f) => n + f.size, 0);
  const tooBig = !!wf && (wf.overCap || pickedSize > wf.cap);
  const mb = (n: number) => `${(n / 1024 / 1024).toFixed(1)} MB`;

  // Confirm and "Status Only" need a loaded draft that still describes the job (no fallback that
  // sends an email nobody was shown).
  const canSend = jcCanSend(seeded.current, curDraft, eStale);
  // What the editor holds, checked — null when it cannot go (the reason is shown under the email).
  const confirmBody = () => {
    const p = jcConfirmPayload(seeded.current, curDraft, eStale,
      { to: splitEmails(eTo), cc: splitEmails(eCc), subject: eSubj },
      { edited: !!bodyRef.current?.edited(), html: bodyRef.current ? bodyRef.current.get() : eBody });
    if ('error' in p) { if (p.error === 'pop_jc_changed') setEStale(true); else setEErr(t(p.error)); return null; }
    setEErr('');
    return p;
  };
  // "Reload the email": the row's newest draft replaces the fields (and the operator's edits).
  const reloadEmail = async () => {
    if (busy) return;
    const rows = await poll();
    const row = rows?.find((x: any) => x.id === curId);
    if (row) fillFrom(curId, row.draft, true);   // a row that left the queue is handled by the effect
  };
  const failed = (e: any) => {
    // 409: the job moved after the draft the fields were filled from — say so and fetch the new draft;
    // the fields are replaced when the operator presses "Reload the email".
    if (e?.status === 409) { setEStale(true); setEErr(''); void poll(); return; }
    // Something in the email itself (no recipient, an emptied body, over the size limit): fixed here.
    if (e?.status === 413) { setEErr(t('pop_too_large')); return; }
    if (e?.status === 400 && !e?.payload?.missingDocs) { setEErr(e?.message || String(e)); return; }
    setBlockingError(`Job #${Number(current.job_no).toLocaleString()}\n\n${e?.message || e}\n\nThe confirmation is still pending — it will pop up again so you can retry.`);
  };

  const send = async (withDocs: boolean) => {
    if (busy) return;
    const ed = confirmBody();
    if (!ed) return;
    // Cancelling the documents must not cancel the announcement — say so before it goes.
    if (wf && !withDocs && !window.confirm(t('jc_status_only_confirm'))) return;
    setBusy(true);
    try {
      const docs = withDocs
        ? { withDocs: true, extraHtml: extra.trim() || undefined, fileIds: tooBig ? [] : picked }
        : { withDocs: false };
      const r: any = await popupsApi.jobConfirmSend(current.id, { ...ed, ...docs });
      showToast(r?.noChange ? t('jc_nochange') : r?.attached ? t('jc_sent_docs', { n: r.attached }) : t('jc_sent'));
      drop(current.id);
    } catch (e: any) { failed(e); } finally { setBusy(false); }
  };

  const onConfirm = async () => {
    if (busy) return;
    if (wf?.linked) return send(true);
    const ed = confirmBody();
    if (!ed) return;
    setBusy(true);
    try {
      const r: any = await popupsApi.jobConfirmSend(current.id, { ...ed, ...(calPlan ? { calendarPlan: calPlan } : {}) });
      // A completion email is parked for approval rather than sent, so say which happened.
      showToast(r?.noChange ? t('jc_nochange') : r?.queued ? t('jc_queued') : t('jc_sent'));
      // The client's email went, but a resource's own finish-date email did not — that needs to be seen.
      if (r?.emailWarning) setBlockingError(`Job #${Number(current.job_no).toLocaleString()}\n\n${r.emailWarning}`);
      drop(current.id);
    } catch (e: any) { failed(e); } finally { setBusy(false); }
  };
  const onRecheck = async () => {
    if (busy) return;
    setBusy(true);
    try { await api.jobConfirmDefer(current.id); } catch { /* still opens the editor */ }
    seeded.current = { id: '', sig: '' };   // when it pops up again, the email starts from the job as it is then
    drop(current.id);
    setBusy(false);
    navigate(`/m/active_jobs?open=${current.job_id}&edit=1`);
  };
  // Revert → restore the previous status and cancel the pending email; refresh the Jobs list so
  // the reverted status shows immediately behind the popup.
  const onRevert = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const r: any = await api.jobConfirmRevert(current.id);
      showToast(t('jc_reverted', { status: plLabel(r?.oldLevel || current.old_level || '') }));
      // detail.jobId lets an OPEN JobDetailPanel re-fetch too (not just the list) — otherwise a
      // stale panel keeps showing (and could re-save) the values the revert just discarded.
      window.dispatchEvent(new CustomEvent('slf:jobs-changed', { detail: { jobId: current.job_id } }));
      seeded.current = { id: '', sig: '' };
      drop(current.id);
    } catch (e: any) { showToast(e?.message || 'Revert failed'); }
    finally { setBusy(false); }
  };

  const jcInp: React.CSSProperties = { width: '100%', padding: '6px 9px', fontSize: 12.5, borderRadius: 7, border: '1px solid var(--border-strong)', background: 'var(--bg-app)', color: 'var(--text-primary)', outline: 'none', boxSizing: 'border-box' };
  const jcLbl: React.CSSProperties = { fontSize: 11, fontWeight: 700, color: 'var(--text-tertiary)', alignSelf: 'center' };
  const kv = (label: string, value: React.ReactNode, strong = false) => (
    <tr>
      <td style={{ padding: '4px 14px 4px 0', color: 'var(--text-tertiary)', whiteSpace: 'nowrap', verticalAlign: 'top', fontSize: 12 }}>{label}</td>
      <td style={{ padding: '4px 0', fontSize: 13, fontWeight: strong ? 700 : 400, color: strong ? 'var(--accent-text)' : 'var(--text-primary)' }}>{value}</td>
    </tr>
  );

  return (
    <div data-jc-popup style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 5500, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div style={{ background: 'var(--bg-surface)', borderRadius: 14, width: curDraft ? 640 : 500, maxWidth: '94vw', maxHeight: '90vh', overflowY: 'auto', boxShadow: 'var(--shadow-md)' }}>
        <div style={{ padding: '18px 22px 10px', borderBottom: '1px solid var(--border)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
            <i className="ti ti-mail-forward" style={{ fontSize: 22, color: 'var(--accent)' }} aria-hidden />
            <div style={{ fontSize: 16, fontWeight: 800, color: 'var(--text-primary)' }}>{t('jc_title')}</div>
            {queue.length > 1 && <span style={{ marginLeft: 'auto', fontSize: 11, fontWeight: 700, color: 'var(--text-tertiary)', background: 'var(--c-gray-100)', borderRadius: 999, padding: '2px 9px' }}>1 / {queue.length}</span>}
          </div>
          <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', marginTop: 8, lineHeight: 1.5 }}>{t(internalOnly ? 'jc_subtitle_internal' : 'jc_subtitle')}</div>
        </div>

        <div style={{ padding: '14px 22px 4px' }}>
          <table style={{ borderCollapse: 'collapse', width: '100%' }}><tbody>
            {kv(t('jc_job'), `#${Number(current.job_no).toLocaleString()} · ${jobTitleLabel(current.specific_job) || '—'}`)}
            {kv(t('jc_client'), client)}
            {/* Both levels as the Jobs screens print them — 'Immigration', an awaiting level's bare word with no symbol
                in front (report c0b2267f; report 22f86d4d had put Calem's symbol there), '3. Review (Client)'
                (LevelLabel). The revert toast above is text only and prints the same word (plLabel). */}
            {kv(t('jc_prev'), current.old_level ? <LevelLabel v={current.old_level} /> : '—')}
            {kv(t('jc_new'), (current.process_level || current.new_level) ? <LevelLabel v={current.process_level || current.new_level} /> : '—', true)}
            {kv(t('jc_finish'), finishNode, !finishChanged)}
            {current.comments ? kv(t('jc_comments'), <span style={{ whiteSpace: 'pre-wrap' }}>{String(current.comments).replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim()}</span>) : null}
          </tbody></table>
        </div>

        {wf?.linked && (
          <div style={{ margin: '10px 22px', padding: '12px 14px', border: '1px solid #A7F3D0', borderRadius: 10, background: '#F0FDF4' }}>
            <div style={{ fontSize: 12.5, fontWeight: 800, color: '#0E7C66', marginBottom: 8 }}>
              <i className="ti ti-paperclip" style={{ marginRight: 5 }} aria-hidden />{t('jc_wf_title')}
            </div>
            {tooBig ? (
              // Owner's rule: over the cap the email carries the LINK, never a partial file list.
              <div style={{ fontSize: 12, color: '#92400E', background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 8, padding: '8px 10px', marginBottom: 8 }}>
                <i className="ti ti-alert-triangle" style={{ marginRight: 5 }} aria-hidden />
                {t('jc_wf_toobig', { size: mb(wf.overCap ? wf.totalSize : pickedSize), cap: mb(wf.cap) })}
              </div>
            ) : wf.files.length ? (
              <div style={{ marginBottom: 8 }}>
                {wf.files.map(f => (
                  <label key={f.id} style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12, padding: '2px 0', cursor: 'pointer' }}>
                    <input type="checkbox" checked={picked.includes(f.id)} style={{ cursor: 'pointer' }}
                      onChange={e => setPicked(p => (e.target.checked ? [...p, f.id] : p.filter(x => x !== f.id)))} />
                    <span style={{ fontWeight: 600 }}>{f.name}</span>
                    <span style={{ color: 'var(--text-tertiary)' }}>{Math.max(1, Math.round(f.size / 1024))} KB · {f.step}</span>
                  </label>
                ))}
                <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 4 }}>{t('jc_wf_total', { n: picked.length, size: mb(pickedSize) })}</div>
              </div>
            ) : (
              <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginBottom: 8 }}>{t('jc_wf_none')}</div>
            )}
            <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-tertiary)' }}>{t('jc_wf_message')}</label>
            <textarea value={extra} onChange={e => setExtra(e.target.value)} rows={5}
              style={{ width: '100%', marginTop: 3, padding: '7px 9px', fontSize: 11.5, fontFamily: 'ui-monospace, monospace', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--bg-app)', color: 'var(--text-primary)', resize: 'vertical' }} />
            <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 4 }}>{t('jc_wf_hint')}</div>
          </div>
        )}

        {String(current?.new_level || '').trim() === '4. Complete' && (
          <CompletionDocs key={current.job_id} jobId={current.job_id} onState={setDocState} onPlan={setCalPlan} />
        )}

        <div style={{ margin: '10px 22px', padding: '10px 12px', background: 'var(--c-gray-50)', border: '1px solid var(--border)', borderRadius: 10, fontSize: 11.5, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
          {internalOnly && (
            <div style={{ fontWeight: 700, color: 'var(--text-primary)' }}>
              <i className="ti ti-users" style={{ marginRight: 5 }} aria-hidden />{t('jc_internal_only')}
            </div>
          )}
          {curDraft ? (
            <div style={{ marginTop: internalOnly ? 6 : 0 }}>
              <div style={{ fontSize: 12, fontWeight: 800, color: 'var(--text-primary)', marginBottom: 7 }}>
                <i className="ti ti-mail" style={{ marginRight: 5 }} aria-hidden />{t('pop_jc_email')}
              </div>
              {eStale && (
                <div role="alert" style={{ marginBottom: 8, padding: '8px 10px', borderRadius: 8, background: '#FEF3C7', border: '1px solid #FCD34D', color: '#92400E', fontSize: 12, lineHeight: 1.5 }}>
                  <div style={{ fontWeight: 800 }}><i className="ti ti-alert-triangle" style={{ marginRight: 5 }} aria-hidden />{t('pop_jc_changed')}</div>
                  <div>{t('pop_jc_changed_hint')}</div>
                  <button type="button" onClick={reloadEmail} disabled={busy}
                    style={{ marginTop: 6, padding: '5px 12px', borderRadius: 7, border: '1px solid #B45309', background: '#fff', color: '#92400E', fontWeight: 700, fontSize: 12, cursor: busy ? 'not-allowed' : 'pointer' }}>
                    <i className="ti ti-refresh" style={{ marginRight: 5 }} aria-hidden />{t('pop_jc_reload')}
                  </button>
                </div>
              )}
              {eReloaded && !eStale && (
                <div style={{ marginBottom: 8, fontSize: 12, fontWeight: 700, color: '#92400E' }}>
                  <i className="ti ti-info-circle" style={{ marginRight: 5 }} aria-hidden />{t('pop_jc_reloaded')}
                </div>
              )}
              <div style={{ display: 'grid', gridTemplateColumns: '58px 1fr', gap: '6px 8px', marginBottom: 8 }}>
                <span style={jcLbl}>{t('pop_from')}</span><span style={{ fontSize: 12, alignSelf: 'center' }}>{curDraft.from}</span>
                <span style={jcLbl}>{t('pop_to')}</span><input value={eTo} onChange={e => { setETo(e.target.value); setEDirty(true); }} style={jcInp} />
                <span style={jcLbl}>{t('pop_cc')}</span><input value={eCc} onChange={e => { setECc(e.target.value); setEDirty(true); }} style={jcInp} />
                <span style={jcLbl}>{t('pop_subject')}</span><input value={eSubj} onChange={e => { setESubj(e.target.value); setEDirty(true); }} style={jcInp} />
              </div>
              <DraftBody key={eBodyKey} ref={bodyRef} html={eBody} onEdit={() => setEDirty(true)}
                style={{ minHeight: 140, maxHeight: 280, fontSize: 13, background: '#fff', color: '#111', padding: '10px 12px' }} />
              {wf?.linked && <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 5 }}>{t('pop_jc_docs_below')}</div>}
              {eErr && <div style={{ fontSize: 12, color: '#B42318', fontWeight: 700, marginTop: 6 }}>{eErr}</div>}
            </div>
          ) : (
            <>
              <div><b style={{ color: 'var(--text-tertiary)' }}>{t('jc_to')}:</b> {to}</div>
              <div><b style={{ color: 'var(--text-tertiary)' }}>{t('jc_cc')}:</b> {cc}</div>
              {/* No draft, no send: Confirm stays off until the email can be shown (Re-check / Revert still work). */}
              <div style={{ color: '#B42318', fontWeight: 700, marginTop: 4 }}>
                {current.draftError ? t('pop_jc_draft_error', { msg: current.draftError }) : t('pop_jc_no_draft')}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span>{t('pop_jc_wait_draft')}</span>
                <button type="button" onClick={() => { void poll(); }} disabled={busy}
                  style={{ padding: '3px 10px', borderRadius: 7, border: '1px solid var(--border-strong)', background: 'var(--bg-surface)', color: 'var(--text-primary)', fontWeight: 700, fontSize: 11.5, cursor: 'pointer' }}>
                  <i className="ti ti-refresh" style={{ marginRight: 4 }} aria-hidden />{t('pop_jc_retry')}
                </button>
              </div>
            </>
          )}
          {!!rc?.resources?.length && (
            <div><b style={{ color: 'var(--text-tertiary)' }}>{t('jc_resources')}:</b> {rc.resources.join(', ')}</div>
          )}
          {/* A completion email carrying the client's documents is parked for approval, not sent on
              Confirm — say so before the click, not only in the toast after it. */}
          {current.queued && (
            <div style={{ fontWeight: 700, color: '#B45309' }}>
              <i className="ti ti-hourglass" style={{ marginRight: 5 }} aria-hidden />{t('jc_approval_first')}
            </div>
          )}
        </div>

        <div style={{ display: 'flex', gap: 8, padding: '8px 22px 20px', flexWrap: 'wrap' }}>
          {!(docState?.gated && docState.missing.length) && <button onClick={onConfirm} disabled={busy || !canSend} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '9px 18px', borderRadius: 9, border: 'none', background: 'var(--accent)', color: '#fff', fontWeight: 700, fontSize: 13, cursor: busy || !canSend ? 'not-allowed' : 'pointer', opacity: busy || !canSend ? 0.55 : 1 }}>
            <i className="ti ti-send" style={{ fontSize: 14 }} aria-hidden />{busy ? t('jc_sending') : t('jc_confirm_send')}
          </button>}
          {wf?.linked && (
            <button onClick={() => send(false)} disabled={busy || !canSend} title={t('jc_status_only_hint')}
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '9px 16px', borderRadius: 9, border: '1px solid var(--border-strong)', background: 'transparent', color: 'var(--text-primary)', fontWeight: 600, fontSize: 13, cursor: busy || !canSend ? 'not-allowed' : 'pointer', opacity: busy || !canSend ? 0.55 : 1 }}>
              <i className="ti ti-mail-off" style={{ fontSize: 14 }} aria-hidden />{t('jc_status_only')}
            </button>
          )}
          <button onClick={onRecheck} disabled={busy} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '9px 16px', borderRadius: 9, border: '1px solid var(--border-strong)', background: 'transparent', color: 'var(--text-primary)', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
            <i className="ti ti-pencil" style={{ fontSize: 14 }} aria-hidden />{t('jc_recheck')}
          </button>
          <button onClick={onRevert} disabled={busy} title={t('jc_revert_hint')} style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6, padding: '9px 16px', borderRadius: 9, border: '1px solid rgba(226,75,74,0.35)', background: 'transparent', color: '#E24B4A', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
            <i className="ti ti-arrow-back-up" style={{ fontSize: 14 }} aria-hidden />{t('jc_revert')}
          </button>
        </div>
      </div>
    </div>
  );
}
